import { useState } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Printer, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import PrintHeader from "@/components/PrintHeader";

const DENOMS = ["$1", "$2", "$3", "$5", "$10", "$20", "$30", "$50"];
const SAFE_DROP_ROWS = 5;
const PAID_OUT_ROWS = 7;
const BACKUP_ROWS = ["Cashier", "Quarters", "Dimes", "Nickels", "Pennies", "Misc Cash", "Total"];
const CLOSING_ROWS = [
  "Instant Lottery",
  "Online Lottery",
  "Loto. Prizes Paid",
  "Money Order",
  "EBT",
  "Verifone C.C.",
  "House A/C",
];

export default function Worksheet() {
  const { user } = useAuth();
  const [copies, setCopies] = useState(1);
  const [rowsPerGrid, setRowsPerGrid] = useState(30);

  const handlePrint = () => {
    window.print();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-4"
    >
      {/* Header controls — hidden when printing */}
      <Card className="border-0 shadow-lg print:hidden" data-testid="worksheet-controls">
        <CardHeader>
          <CardTitle className="font-mono flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Daily Worksheet (Printable)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs font-mono text-muted-foreground leading-relaxed">
            A blank single-page worksheet for clerks to <span className="text-primary font-bold">hand-write</span> running numbers,
            safe drops, paid-outs, and closing data — before entering them into the app. Print one per shift.
          </p>
          <div className="flex flex-wrap items-center gap-3 pt-2">
            <Button
              onClick={handlePrint}
              className="btn-primary flex items-center gap-2"
              data-testid="worksheet-print-btn"
            >
              <Printer className="w-4 h-4" /> Print Worksheet
            </Button>
            <div className="flex items-center gap-2 text-xs font-mono">
              <label htmlFor="ws-copies" className="text-muted-foreground">Copies:</label>
              <select
                id="ws-copies"
                value={copies}
                onChange={(e) => setCopies(parseInt(e.target.value) || 1)}
                className="bg-secondary border border-border rounded px-2 py-1 font-mono text-xs"
                data-testid="worksheet-copies-select"
              >
                {[1, 2, 3, 5, 10].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono">
              <label htmlFor="ws-rows" className="text-muted-foreground">Rows per grid:</label>
              <select
                id="ws-rows"
                value={rowsPerGrid}
                onChange={(e) => setRowsPerGrid(parseInt(e.target.value) || 20)}
                className="bg-secondary border border-border rounded px-2 py-1 font-mono text-xs"
                data-testid="worksheet-rows-select"
              >
                {[20, 25, 30, 35].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Printable worksheet — repeated `copies` times */}
      <div className="worksheet-print-area">
        {Array.from({ length: copies }).map((_, i) => (
          <WorksheetPage
            key={i}
            storeName={user?.store_name || ""}
            pageIdx={i}
            rows={rowsPerGrid}
          />
        ))}
      </div>

      {/* Print-only CSS */}
      <style>{`
        .worksheet-page {
          background: white;
          color: black;
          padding: 0.25in 0.25in;
          font-family: 'Courier New', monospace;
          font-size: 10px;
          page-break-after: always;
        }
        .worksheet-page:last-child { page-break-after: auto; }
        .ws-header { display: flex; justify-content: space-between; align-items: flex-end; gap: 10px; margin-bottom: 2px; }
        .ws-header-field { flex: 1; display: flex; align-items: baseline; gap: 4px; }
        .ws-header-field .lbl { font-weight: bold; font-size: 11px; white-space: nowrap; }
        .ws-header-field .line { flex: 1; border-bottom: 1px solid #000; height: 16px; }
        .ws-running-wrapper { display: grid; grid-template-columns: 1fr 1fr; gap: 3px; margin-bottom: 2px; }
        .ws-running-wrapper .ws-table { border: 2px solid #000; }
        .ws-running-title { background: #000; color: #fff; font-weight: bold; font-size: 12px; text-align: center; padding: 2px 0; margin: 0 0 2px 0; letter-spacing: 1px; }
        .ws-table { border-collapse: collapse; width: 100%; }
        .ws-table th, .ws-table td { border: 1px solid #000; padding: 0; text-align: center; vertical-align: middle; }
        .ws-table th { background: #f0f0f0; font-weight: bold; font-size: 9px; padding: 1px 2px; height: 14px; }
        .ws-table .row-cell { height: 22px; }
        .ws-section-title { background: #000; color: white; font-weight: bold; font-size: 11px; text-align: center; padding: 1px 0; margin: 2px 0 0 0; letter-spacing: 0.5px; }
        .ws-section-title.split { background: transparent; color: black; border-top: 1px solid #000; padding-top: 2px; }
        .ws-mid-row { display: grid; grid-template-columns: 1.1fr 1fr; gap: 4px; }
        .ws-bottom-row { display: grid; grid-template-columns: 0.85fr 1.2fr 0.9fr; gap: 4px; }
        .ws-label-col { width: 28%; }
        .ws-amount-col { width: 50%; }
        .ws-bag-col { width: 16%; }
        .ws-clerk-col { width: 18%; }
        .ws-money-input { height: 18px; }

        @media print {
          @page { size: letter portrait; margin: 0.15in; }
          html, body {
            background: white !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Kill ALL ancestor offsets (layout main padding, space-y margins):
             any offset above the sheet pushes its bottom row (Backup Change /
             Lottery Data / Closing Data) past the page boundary onto page 2. */
          main { padding: 0 !important; margin: 0 !important; }
          .worksheet-print-area { background: white; margin: 0 !important; padding: 0; }
          .worksheet-page {
            padding: 0.05in 0.1in;
            box-shadow: none;
            border: none;
            margin: 0;
            /* Fit within ONE letter portrait page. Letter is 11in; minus @page
               margin 0.15in × 2 = 10.7in printable. We use 10.5in to leave a
               buffer so subpixel math never spills into a blank 2nd
               page. overflow: hidden clips anything extra. */
            display: flex;
            flex-direction: column;
            font-size: 10px;
            height: 10.5in;
            max-height: 10.5in;
            overflow: hidden;
            page-break-after: always;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          /* Single-copy case: only one worksheet-page exists AND is :last-child.
             Must NOT force a page break after it (else browser prints a blank
             2nd page). */
          .worksheet-page:last-child {
            page-break-after: auto;
            break-after: avoid;
          }
          /* Hide app chrome */
          nav, header, footer, .print\\:hidden { display: none !important; }

          /* Header + section titles take their natural height */
          .ws-section-title { margin: 2px 0 1px 0; padding: 0; font-size: 10px; line-height: 1.2; flex: 0 0 auto; }
          .ws-section-title.split { padding-top: 2px; }
          .ws-running-title { font-size: 12px; padding: 2px 0; margin: 0 0 2px 0; line-height: 1.2; flex: 0 0 auto; letter-spacing: 1.5px; }
          .ws-header { margin-bottom: 2px; flex: 0 0 auto; }
          .ws-header-field .lbl { font-size: 11px; }
          .ws-header-field .line { height: 16px; }

          /*
           * VERTICAL SPACE DISTRIBUTION
           * All three "table blocks" (running numbers, mid row, bottom row) grow
           * proportionally to their row count so no whitespace is left at the
           * bottom of the page and every row across the page ends up the same
           * height. The flex-grow numbers are:
           *   - Running Numbers: --ws-running-rows (30 by default, user-tunable)
           *   - Mid row: 7 (max of SafeDrops=5, CashPaidOut=7)
           *   - Bottom row: 9 (max of BackupChange=7, LotteryData=8, ClosingData=9)
           */
          .ws-running-wrapper {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 3px;
            margin-bottom: 2px;
            flex: var(--ws-running-rows, 30) 1 0;
            min-height: 0;
          }
          .ws-mid-row {
            gap: 4px;
            flex: 7 1 0;
            min-height: 0;
            margin-top: 2px;
          }
          .ws-bottom-row {
            gap: 4px;
            margin-top: 3px !important;
            flex: 9 1 0;
            min-height: 0;
          }

          /*
           * Each "block" inside the grid rows is a bare <div> containing a
           * .ws-section-title + a .ws-table. Turn each into a flex-column so
           * the section title stays fixed height and the table fills the rest.
           */
          .ws-running-wrapper > div,
          .ws-mid-row > div,
          .ws-bottom-row > div {
            display: flex;
            flex-direction: column;
            min-height: 0;
          }
          .ws-running-wrapper > div > div:first-child {
            font-size: 10px;
            margin-bottom: 1px;
            flex: 0 0 auto;
          }

          /* Tables inside the growing blocks fill their block vertically */
          .ws-running-wrapper .ws-table,
          .ws-mid-row .ws-table,
          .ws-bottom-row .ws-table {
            flex: 1 1 auto;
            height: 100%;
          }
          .ws-running-wrapper .ws-table {
            border: 1.5px solid #000;
          }
          .ws-running-wrapper .ws-table tbody,
          .ws-mid-row .ws-table tbody,
          .ws-bottom-row .ws-table tbody {
            height: 100%;
          }

          /*
           * The 'height: 1%' trick: inside a table with height:100%, rows with
           * height:1% distribute evenly. This gives all rows across the page
           * approximately equal height, so extra whitespace is absorbed.
           */
          .ws-running-wrapper .ws-table .row-cell,
          .ws-mid-row .ws-table .row-cell,
          .ws-bottom-row .ws-table .row-cell {
            height: 1%;
          }
          .ws-table th {
            height: 14px;
            font-size: 9px;
            padding: 1px 2px;
          }
          .ws-mid-row .ws-table th,
          .ws-bottom-row .ws-table th {
            height: 12px;
            font-size: 8px;
            padding: 0 1px;
          }
          .ws-mid-row .ws-table .row-cell,
          .ws-bottom-row .ws-table .row-cell {
            font-size: 9px;
          }
          .ws-money-input { height: 14px; }
        }
      `}</style>
    </motion.div>
  );
}

function WorksheetPage({ storeName, pageIdx, rows }) {
  return (
    <div
      className="worksheet-page"
      data-testid={`worksheet-page-${pageIdx}`}
      style={{ "--ws-running-rows": rows }}
    >
      <PrintHeader />
      {/* Header */}
      <div className="ws-header">
        <div className="ws-header-field" style={{ flex: 2 }}>
          <span className="lbl">Store Name:</span>
          <span className="line">{storeName ? <span style={{ paddingLeft: 4 }}>{storeName}</span> : ""}</span>
        </div>
        <div className="ws-header-field">
          <span className="lbl">Day:</span>
          <span className="line"></span>
        </div>
        <div className="ws-header-field">
          <span className="lbl">Date:</span>
          <span className="line"></span>
        </div>
      </div>

      {/* Running Numbers — focal point: bold title bar above two side-by-side grids */}
      <div className="ws-section-title ws-running-title">Running Numbers</div>
      <div className="ws-running-wrapper">
        <RunningGrid label="Opening" rows={rows} />
        <RunningGrid label="Closing" rows={rows} />
      </div>

      {/* Safe Drops + Cash Paid Out */}
      <div className="ws-mid-row">
        <SafeDropsBlock />
        <CashPaidOutBlock />
      </div>

      {/* Backup Change + Lottery Data + Closing Data */}
      <div className="ws-bottom-row" style={{ marginTop: 4 }}>
        <BackupChangeBlock />
        <LotteryDataBlock />
        <ClosingDataBlock />
      </div>
    </div>
  );
}

function RunningGrid({ label, rows = 20 }) {
  return (
    <div>
      <div style={{ fontWeight: "bold", fontSize: 10, textAlign: "center", marginBottom: 1 }}>
        {label}
      </div>
      <table className="ws-table">
        <thead>
          <tr>
            {DENOMS.map((d) => (
              <th key={d} style={{ width: `${100 / DENOMS.length}%` }}>{d}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, r) => (
            <tr key={r}>
              {DENOMS.map((d) => <td key={d} className="row-cell">&nbsp;</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SafeDropsBlock() {
  return (
    <div>
      <div className="ws-section-title">Safe Drops</div>
      <table className="ws-table">
        <thead>
          <tr>
            <th className="ws-bag-col">Bag#</th>
            <th>Amount</th>
            <th className="ws-clerk-col">Clerk</th>
            <th className="ws-bag-col">Bag#</th>
            <th>Amount</th>
            <th className="ws-clerk-col">Clerk</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: SAFE_DROP_ROWS }).map((_, r) => (
            <tr key={r}>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CashPaidOutBlock() {
  return (
    <div>
      <div className="ws-section-title">Cash Paid Out Only</div>
      <table className="ws-table">
        <thead>
          <tr>
            <th>Vendor</th>
            <th style={{ width: "30%" }}>Amount</th>
            <th style={{ width: "20%" }}>Clerk</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: PAID_OUT_ROWS }).map((_, r) => (
            <tr key={r}>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BackupChangeBlock() {
  return (
    <div>
      <div className="ws-section-title">Backup Change</div>
      <table className="ws-table">
        <thead>
          <tr>
            <th>Shift</th>
            <th>1st</th>
            <th>2nd</th>
            <th>End</th>
          </tr>
        </thead>
        <tbody>
          {BACKUP_ROWS.map((label) => (
            <tr key={label}>
              <td className="row-cell" style={{ fontWeight: label === "Total" ? "bold" : "normal", textAlign: "left", paddingLeft: 4 }}>{label}</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LotteryDataBlock() {
  return (
    <div>
      <div className="ws-section-title">Lottery Data</div>
      <table className="ws-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Opening</th>
            <th>Delivery</th>
            <th>Tkt.Add</th>
            <th>Return</th>
            <th>LVM-1</th>
            <th>LVM-2</th>
            <th>Ending</th>
          </tr>
        </thead>
        <tbody>
          {DENOMS.map((d) => (
            <tr key={d}>
              <td className="row-cell" style={{ fontWeight: "bold", paddingLeft: 3, textAlign: "left" }}>{d}</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
              <td className="row-cell">&nbsp;</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ClosingDataBlock() {
  return (
    <div>
      <div className="ws-section-title">Closing Data</div>
      <table className="ws-table">
        <tbody>
          {CLOSING_ROWS.map((label) => (
            <tr key={label}>
              <td className="row-cell" style={{ textAlign: "left", paddingLeft: 3 }}>{label}</td>
              <td className="row-cell" style={{ width: "45%" }}>&nbsp;</td>
            </tr>
          ))}
          {/* Extra blank row for "Cash Drawer / Other" totals */}
          <tr>
            <td className="row-cell" style={{ textAlign: "left", paddingLeft: 3, fontWeight: "bold" }}>Total</td>
            <td className="row-cell">&nbsp;</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
